import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import useForm from "../common/AsyncFormBox/useForm";
import useFormValidation from "../common/AsyncFormBox/useFormValidation";
import { isValidBankAccount, normalizeBankAccount } from "../../utils/BankAccountUtils";
import { MarketReasonCodeLabels } from "../../config/MarketConfigs";

const DEFAULT_HELPER_TEXT =
  "Český formát (číslo účtu/kód banky) nebo IBAN pro zahraniční účty. Po prodeji vám organizátor pošle peníze na tento účet.";

/**
 * Text input for seller payout bank account (CZ format or IBAN-lite).
 * Validates inline while typing and blocks AsyncFormBox submit when invalid.
 *
 * @param {Object} props
 * @param {string} [props.name="bankAccount"] - Form field name.
 * @param {React.ReactNode} [props.label] - Field label.
 * @param {boolean} [props.required=true] - Whether field is required.
 * @param {string} [props.placeholder] - Input placeholder.
 * @param {string} [props.helperText] - Helper text when there is no validation error.
 * @param {string} [props.defaultValue=""] - Initial value.
 * @param {Object} props - Remaining props spread to TextField.
 */
function BankAccountInput({
  name = "bankAccount",
  label = "Bankovní účet pro výplatu",
  required = true,
  placeholder = "123456789/0100 nebo CZ65 0800 0000 0019 2000 1454",
  helperText = DEFAULT_HELPER_TEXT,
  defaultValue = "",
  onChange,
  ...rest
}) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState(null);
  const form = useForm();

  const validateValue = useCallback(
    (rawValue, { showRequiredError = false } = {}) => {
      const trimmed = typeof rawValue === "string" ? rawValue.replace(/\s+/g, "").trim() : "";

      if (!trimmed) {
        if (required && showRequiredError) {
          setError("Bankovní účet je povinný.");
          return false;
        }
        setError(null);
        return !required;
      }

      if (!isValidBankAccount(rawValue)) {
        setError(MarketReasonCodeLabels.INVALID_BANK_ACCOUNT);
        return false;
      }

      setError(null);
      return true;
    },
    [required],
  );

  function handleChange(event) {
    const nextValue = event.target.value;
    setValue(nextValue);
    form.setValue?.(name, nextValue);
    validateValue(nextValue);
    onChange?.(event);
  }

  const validate = useCallback(
    (formValues) => {
      const rawValue = formValues[name];
      const isValid = validateValue(rawValue, { showRequiredError: true });
      if (!isValid) return false;

      const normalized = normalizeBankAccount(rawValue);
      if (normalized) {
        form.setValue?.(name, normalized);
      }
      return true;
    },
    [form, name, validateValue],
  );

  useFormValidation(name, validate, [validate]);

  return (
    <TextField
      {...rest}
      name={name}
      value={value}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      required={required}
      error={!!error}
      helperText={error || helperText}
    />
  );
}

BankAccountInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.node,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
};

export default BankAccountInput;
